import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Avatar,
  TextField,
  ListSubheader,
  InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import SearchIcon from "@mui/icons-material/Search";
import { useGetAssignees } from "../../api/collection";
import { useMemo, useState } from "react";

const DropdownIcon = (props) => (
  <Icon
    icon="eva:arrow-ios-downward-fill"
    width="18"
    height="18"
    {...props}
  />
);

function AssigneeFilter({ value = [], onChange }) {
  const { assignees = [] } = useGetAssignees({});
  const [search, setSearch] = useState("");

  const uniqueAssignees = useMemo(
    () =>
      Array.from(
        new Map(assignees.map((a) => [a._id, a])).values()
      ),
    [assignees]
  );

  // Search filter
  const filteredAssignees = useMemo(() => {
    return uniqueAssignees.filter((a) =>
      a.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [uniqueAssignees, search]);

  const count = value.length;

  return (
    <FormControl size="small" sx={{ minWidth: 120 }}>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(e.target.value)}
        IconComponent={DropdownIcon}
        input={<OutlinedInput />}
        displayEmpty
        renderValue={() => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            <span>Assigned To</span>

            {count > 0 && (
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "#6C63FF",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        )}
        sx={{
          height: 38,
          width: count > 0 ? 145 : 120,
          borderRadius: "10px",
          backgroundColor: "var(--app-surface)",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
          },
        }}
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
          PaperProps: {
            sx: {
              maxHeight: 290,
              borderRadius: "10px",
              mt: 1,
              border: "1px solid var(--app-border)",
              boxShadow: "var(--app-shadow)",
            },
          },
          MenuListProps: {
            sx: {
              maxHeight: 290,
              overflowY: "auto",
              pr: 1,
            },
          },
          disableScrollLock: true,
        }}
      >
        {/* Search Field */}
        <ListSubheader
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: "var(--app-surface)",
          }}
        >
          <TextField
            fullWidth
            autoFocus
            size="small"
            placeholder="Search assignee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "12px",

                  "& fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&:hover fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "var(--app-border)",
                    borderWidth: "1px",
                  },

                  "&.Mui-focused": {
                    boxShadow: "none",
                  },
                },
              }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: "var(--app-text-muted)" }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </ListSubheader>

        {/* Filtered List */}
        {filteredAssignees.length > 0 ? (
          filteredAssignees.map((a) => (
            <MenuItem
              key={a._id}
              value={a._id}
              sx={{
                maxWidth: 260,
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Checkbox
                checked={value.includes(a._id)}
                size="small"
                sx={{
                  mr: 1,
                  color: "var(--app-border)",
                  "&.Mui-checked": {
                    color: "#5751E9",
                  },
                  "& .MuiSvgIcon-root": {
                    borderRadius: "8px",
                    fontSize: 20,
                  },
                }}
              />

              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  src={a.profile || undefined}
                  sx={{
                    width: 20,
                    height: 20,
                    fontSize: "11px",
                    backgroundColor: "#E6E6E6",
                    border: "1px solid var(--app-border)",
                  }}
                >
                  {!a.profile &&
                    a.name
                      ?.split(" ")
                      .map((w) => w[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                </Avatar>

                <ListItemText
                  primary={
                    a.name.length > 17
                      ? `${a.name.slice(0, 17)}...`
                      : a.name
                  }
                  primaryTypographyProps={{
                    fontSize: "14px",
                    color: "var(--app-text)",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No results found</MenuItem>
        )}
      </Select>
    </FormControl>
  );
}

export default AssigneeFilter;